import { useRef, useEffect, useState, useMemo, useCallback, useContext } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DotsLoader from '../ui/DotsLoader';
import useBooking from '../../hooks/useBooking';
import useInternalBooking from '../../hooks/useInternalBooking';
import useEntertainment from '../../hooks/useEntertainment';
import useBranch from '../../hooks/useBranch';
import useResource from '../../hooks/useResource';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { BookingProvider } from '../../context/bookingContext';
import SocketIOContext from '../../context/socketIOContext';
import CreateBookingModal from '../Bookings/CreateBookingsModal/CreateBookingModal';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import AddIcon from '@mui/icons-material/Add';
import TodayIcon from '@mui/icons-material/Today';
import BlockIcon from '@mui/icons-material/Block';
import EventNoteIcon from '@mui/icons-material/EventNote';
import GridLasertag from '../Bookings/BookingGrid/Lasertag/GridLasertag';
import GridAxe from '../Bookings/BookingGrid/Axe/GridAxe';
import GridExit from '../Bookings/BookingGrid/Exit/GridExit';
import GridGolfOrdered from '../Bookings/BookingGrid/Golf/GridGolfOrdered';
import GridBashOrdered from '../Bookings/BookingGrid/Bash/GridBashOrdered';
import BookingTable from '../Bookings/components/BookingTable';
import GridPaintball from '../Bookings/BookingGrid/Paintball/GridPaintball';
import GridPixel from '../Bookings/BookingGrid/Pixel/GridPixel';
import GridKaraoke from '../Bookings/BookingGrid/Karaoke/GridKaraoke';
import GridRallye from '../Bookings/BookingGrid/Rallye/GridRallye';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';
import { useAuth } from '../../context/authContext';
import { useEntertainment as useEntertainmentContext } from '../../context/entertainmentContext';
import CreateBlockerModal from '../Bookings/components/CreateBlockerModal';
import LaserZoneGameStatus from '../Bookings/components/LaserZoneGameStatus';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { toast } from 'react-toastify';
/**
 * Assigns each booking a row index (`i`) for the grid display.
 * Resource-aware: bookings sharing a resourceId prefer the same row,
 * keeping e.g. escape-room bookings visually grouped by room.
 */
const assignBookingRows = bookings => {
  const rows = {};
  const rowResource = {};
  const result = [];
  let rowCount = 0;

  for (const booking of bookings) {
    const startTime =
      booking.bookingType === 'RegiondoBooking' ? booking.eventStart : booking.startTime;
    const endTime =
      booking.bookingType === 'RegiondoBooking' ? booking.eventEnd : booking.endTime;
    const rid = booking.resourceId?._id || booking.resourceId || null;
    const ridStr = rid ? String(rid) : null;
    const startDate = new Date(startTime);

    let bookingRow = 0;

    if (ridStr) {
      for (let i = 0; i < rowCount; i++) {
        if (rowResource[i + 1] === ridStr && rows[i + 1] <= startDate) {
          bookingRow = i + 1;
          rows[i + 1] = new Date(endTime);
          break;
        }
      }
    }

    if (!bookingRow) {
      for (let i = 0; i < rowCount; i++) {
        if (rows[i + 1] <= startDate) {
          bookingRow = i + 1;
          rows[i + 1] = new Date(endTime);
          if (ridStr) rowResource[i + 1] = ridStr;
          break;
        }
      }
    }

    if (!bookingRow) {
      rowCount++;
      rows[rowCount] = new Date(endTime);
      bookingRow = rowCount;
      if (ridStr) rowResource[rowCount] = ridStr;
    }

    result.push({ i: bookingRow, ...booking });
  }

  return { bookingsShown: result, rowCount };
};

function Bookings() {
  const isWeekNavigationVisible = useMediaQuery('(min-width:300px)');
  const isDayNavigationVisible = useMediaQuery('(min-width:230px)');
  const isButtonTextVisible = useMediaQuery('(min-width:260px)');
  const { checkRight, preferences } = useAuth();
  const canOverbook = useMemo(
    () => checkRight('internalBooking:special:overbook'),
    [checkRight]
  );
  const showWeekdayInDatePicker = preferences?.showWeekdayInBookingsView !== false;
  const { setCurrentEntertainment, setCurrentView } = useEntertainmentContext();
  const [canBlock, setCanBlock] = useState(false);
  // Users with the elevated blockDirect right create blockers directly — either
  // company-wide or, with blockDirectOwn, for their assigned branches (the
  // backend enforces the branch scope); users with only `block` submit a blocker
  // request that must be approved.
  const canBlockDirect = useMemo(
    () =>
      checkRight('internalBooking:special:blockDirect') ||
      checkRight('internalBooking:special:blockDirectOwn'),
    [checkRight]
  );
  const [bookings, setBookings] = useState([]);
  const [capacity, setCapacity] = useState(null);
  const [rowCount, setRowCount] = useState(0);
  const [date, setDate] = useState(() => {
    const storedDate = sessionStorage.getItem('date');
    return storedDate ? dayjs(storedDate) : dayjs();
  });
  const [entertainmentOptions, setEntertainmentOptions] = useState([]);
  const [entertainment, setEntertainment] = useState('');
  const [entertainmentType, setEntertainmentType] = useState('');
  const [branchOptions, setBranchOptions] = useState([]);
  const [branch, setBranch] = useState('');
  const [slotCapacity, setSlotCapacity] = useState(0);
  const [showCanceled, setShowCanceled] = useState(false);
  const [tableView, setTableView] = useState(() => {
    const storedValue = sessionStorage.getItem('tableView');
    return storedValue === 'true';
  });
  const [currentFilter, setCurrentFilter] = useState('all');
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [modalBookingId, setModalBookingId] = useState(null);
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [showInternalBookings, setShowInternalBookings] = useState(null);
  const [blockerModalOpen, setBlockerModalOpen] = useState(false);
  const [modalBorderColor, setModalBorderColor] = useState(null);
  const [shiftConfirmation, setShiftConfirmation] = useState({
    open: false,
    booking: null,
    newStartTime: null,
    // Whether the drop target exceeds the slot capacity — only then is the
    // overbooking question put in front of the user.
    requiresOverbook: false,
    overbookedBy: 0,
  });
  const [overbookConfirmed, setOverbookConfirmed] = useState(false);
  const [pendingShift, setPendingShift] = useState(null);
  const [availableShiftTimes, setAvailableShiftTimes] = useState(null);
  const availableShiftTimesRef = useRef(null);
  const [isLoadingShiftTimes, setIsLoadingShiftTimes] = useState(false);
  const [draggedBookingDuration, setDraggedBookingDuration] = useState(0);
  const [assignResourceModalOpen, setAssignResourceModalOpen] = useState(false);
  const [useVerticalForInternal, setUseVerticalForInternal] = useState(() => {
    const pref = preferences?.internalVerticalShowDetails;
    return typeof pref === 'boolean' ? pref : true;
  });
  const [isIntervalModeActive, setIsIntervalModeActive] = useState(false);
  const [isDayDataLoading, setIsDayDataLoading] = useState(false);
  const [loadedDayKey, setLoadedDayKey] = useState(null);
  const [dayDataError, setDayDataError] = useState(null);
  const dayDataFetchIdRef = useRef(0);
  const filterBarRef = useRef(null);

  useEffect(() => {
    const pref = preferences?.internalVerticalShowDetails;
    if (typeof pref === 'boolean') {
      setUseVerticalForInternal(pref);
    }
  }, [preferences?.internalVerticalShowDetails]);

  // Keep --booking-filter-bar-bottom in sync with the actual rendered position of the filter bar.
  // BookingGridBase reads this variable to position the sticky capacity bar correctly.
  useEffect(() => {
    const el = filterBarRef.current;
    if (!el) return;

    let rafId = null;
    const updateVar = () => {
      const rect = el.getBoundingClientRect();
      document.documentElement.style.setProperty(
        '--booking-filter-bar-bottom',
        `${rect.bottom}px`
      );
    };

    const scheduleUpdate = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateVar();
      });
    };

    updateVar();
    const observer = new ResizeObserver(scheduleUpdate);
    observer.observe(el);
    window.addEventListener('scroll', scheduleUpdate, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', scheduleUpdate);
      if (rafId) cancelAnimationFrame(rafId);
      document.documentElement.style.removeProperty('--booking-filter-bar-bottom');
    };
  }, []);

  // Used to prevent race conditions with drag-and-drop actions.
  // Each drag start increments the counter, and async operations from previous drags will not update the state.
  const dragSessionId = useRef(0);
  const prefetchedTimes = useRef(null);

  const {
    getBookingsByEntertainmentDay,
    getFilteredBookingsByEntertainmentDay,
    getResourcesBySingleEntertainmentDay,
  } = useBooking();
  const {
    getInternalBookingsByEntertainmentDay,
    getFilteredInternalBookingsByEntertainmentDay,
    shiftBooking,
    getAvailableShiftTimes,
    getResourcesByEntertainmentsDay,
    error: internalBookingError,
  } = useInternalBooking();
  const { getAllEntertainmentsByBranch } = useEntertainment();
  const { getBranches } = useBranch();
  const { getResourcesByEntertainmentId } = useResource();
  const [lastError, setLastError] = useState(null);
  const theme = useTheme();
  const [currentShiftOffset, setCurrentShiftOffset] = useState(0);
  const [resourceOffsets, setResourceOffsets] = useState({});

  const normalizeShiftTimesResponse = res => {
    return Array.isArray(res) ? res : [];
  };

  // Stable refs for values read inside useCallback handlers
  const bookingsRef = useRef(bookings);
  bookingsRef.current = bookings;
  const socketCtx = useContext(SocketIOContext);
  const fetchBookingsRef = useRef(null);
  const latestRef = useRef({});
  latestRef.current.entertainmentType = entertainmentType;
  latestRef.current.entertainment = entertainment;
  latestRef.current.entertainmentOptions = entertainmentOptions;
  latestRef.current.capacity = capacity;
  latestRef.current.resourceOffsets = resourceOffsets;
  latestRef.current.date = date;
  latestRef.current.currentFilter = currentFilter;
  latestRef.current.showCanceled = showCanceled;
  useEffect(() => {
    // TODO: Move this effect to Resource specific components/generic resource component
    if (!entertainment) {
      setResourceOffsets({});
      return;
    }

    let isActive = true;
    const fetchResources = async () => {
      try {
        const resources = await getResourcesByEntertainmentId(entertainment);
        if (!isActive) {
          return;
        }
        const offsetMap = {};
        (resources || []).forEach(resource => {
          const key =
            (resource?._id && resource._id.toString ? resource._id.toString() : resource?._id) ||
            '';
          if (!key) {
            return;
          }
          offsetMap[key] = resource.startOffsetMinutes || 0;
        });
        setResourceOffsets(offsetMap);
      } catch (error) {
        console.error('Error fetching resources for offsets:', error);
        if (isActive) {
          setResourceOffsets({});
        }
      }
    };

    fetchResources();

    return () => {
      isActive = false;
    };
  }, [entertainment]);

  useEffect(() => {
    if (internalBookingError && internalBookingError !== lastError) {
      setLastError(internalBookingError);
    }
  }, [internalBookingError, lastError]);

  const filterOptions = [
    { id: 'all', label: 'Alle' },
    { id: 'withUpgrades', label: 'Mit Upgrade' },
    { id: 'childBirthday', label: 'Nur KiBu' },
    { id: 'bachelorParty', label: 'Nur JGA' },
    { id: 'bag', label: 'Wundertüte' },
  ];

  const currentDayKey = useMemo(() => {
    const d = date?.format ? date.format('YYYY-MM-DD') : '';
    return `${branch || ''}|${entertainment || ''}|${d}|${currentFilter}|${showCanceled}`;
  }, [branch, entertainment, date, currentFilter, showCanceled]);

  const getFilterQuery = currentFilter => {
    switch (currentFilter) {
      case 'withUpgrades':
        return {
          $or: [
            { 'specialUpgrades.0': { $exists: true, $ne: 'insurance' } },
            { 'specialUpgrades.1': { $exists: true } },
          ],
        };
      case 'childBirthday':
        return { specialUpgrades: 'childBirthday' };
      case 'bachelorParty':
        return { specialUpgrades: 'bachelorParty' };

      case 'bag':
        return { specialUpgrades: 'bag' };
      default:
        return {};
    }
  };

  // Internal bookings use tags (hasPackage, childBirthday, bachelorParty, iconBagYellow)
  // not specialUpgrades like Regiondo. Pass this filter to getFilteredInternalBookingsByEntertainmentDay.
  const getFilterQueryForInternalBookings = currentFilter => {
    switch (currentFilter) {
      case 'withUpgrades':
        return {
          $or: [
            { tags: 'hasPackage' },
            { 'selectedPackage.tags': 'hasPackage' },
            { 'upsells.tags': 'hasPackage' },
          ],
        };
      case 'childBirthday':
        return {
          $or: [
            { tags: { $in: ['childBirthday', 'iconCakeOrange', 'childBirthdayFull', 'iconCakeRed'] } },
            { 'selectedPackage.tags': { $in: ['childBirthday', 'iconCakeOrange', 'childBirthdayFull', 'iconCakeRed'] } },
            { 'upsells.tags': { $in: ['childBirthday', 'iconCakeOrange', 'childBirthdayFull', 'iconCakeRed'] } },
          ],
        };
      case 'bachelorParty':
        return {
          $or: [
            { tags: 'bachelorParty' },
            { 'selectedPackage.tags': 'bachelorParty' },
            { 'upsells.tags': 'bachelorParty' },
          ],
        };
      case 'bag':
        return {
          $or: [
            { tags: { $in: ['bag', 'iconBagYellow'] } },
            { 'selectedPackage.tags': { $in: ['bag', 'iconBagYellow'] } },
            { 'upsells.tags': { $in: ['bag', 'iconBagYellow'] } },
          ],
        };
      default:
        return {};
    }
  };

  const isFirstRender = useRef(true);

  const isCreateBookingModalOpenRef = useRef(isCreateBookingModalOpen);

  useEffect(() => {
    isCreateBookingModalOpenRef.current = isCreateBookingModalOpen;
  }, [isCreateBookingModalOpen]);

  // Load the capacity panel data for the currently selected entertainment + day.
  // Used by the full day fetch and by the debounced refresh after socket deltas.
  const loadCapacityData = async () => {
    const currentEntertainment = entertainmentOptions.find(ent => ent.id === entertainment);

    let capacityData = null;
    if (currentEntertainment?.label && branch) {
      try {
        const internalCapacityList = await getResourcesByEntertainmentsDay({
          date: date.format('YYYY-MM-DD'),
          branches: [branch],
          entertainments: [currentEntertainment.label],
        });
        capacityData =
          Array.isArray(internalCapacityList) && internalCapacityList.length > 0
            ? { success: true, data: internalCapacityList[0] }
            : null;
      } catch {
        capacityData = null;
      }
    }

    if (!capacityData) {
      capacityData = await getResourcesBySingleEntertainmentDay({
        date: date.format('YYYY-MM-DD'),
        entertainmentId: entertainment,
      });
    }

    return capacityData;
  };

  const loadCapacityDataRef = useRef(null);
  loadCapacityDataRef.current = loadCapacityData;

  // function to fecth bookings
  const fetchBookings = async (options = {}) => {
    const { showDayLoadingState = false, loadKey = null } = options;
    const fetchId = ++dayDataFetchIdRef.current;

    if (showDayLoadingState) {
      setIsDayDataLoading(true);
      setDayDataError(null);
    }

    let regiondoBookings = [];
    let internalBookings = [];

    try {
      // Fetch bookings based on settings
      if (entertainmentOptions.some(e => e.id === entertainment)) {
        const currentEntertainment = entertainmentOptions.find(e => e.id === entertainment);

        // Apply filters for RegiondoBookings
        if (currentEntertainment.showRegiondoBookings) {
          if (currentFilter === 'all') {
            regiondoBookings = await getBookingsByEntertainmentDay(
              entertainment,
              date.format('YYYY-MM-DD'),
              showCanceled
            );
          } else {
            const filter = getFilterQuery(currentFilter);
            regiondoBookings = await getFilteredBookingsByEntertainmentDay(
              entertainment,
              date.format('YYYY-MM-DD'),
              filter,
              showCanceled
            );
          }
        }

        // Apply filters for InternalBookings
        if (currentEntertainment.showInternalBookings) {
          if (currentFilter === 'all') {
            internalBookings = await getInternalBookingsByEntertainmentDay(
              entertainment,
              date.format('YYYY-MM-DD'),
              showCanceled
            );
          } else {
            const filter = getFilterQueryForInternalBookings(currentFilter);
            internalBookings = await getFilteredInternalBookingsByEntertainmentDay(
              entertainment,
              date.format('YYYY-MM-DD'),
              filter,
              showCanceled
            );
          }
        }
      }

      // Add bookingType to differentiate
      const allBookings = [
        ...regiondoBookings.map(b => ({ ...b, bookingType: 'RegiondoBooking' })),
        ...internalBookings.map(b => ({ ...b, bookingType: 'InternalBooking' })),
      ];

      // Blockers come back with their full (possibly multi-day) range and are
      // trimmed to the part of the viewed day the grid covers (09:00–23:59) —
      // placed as-is, a blocker running over several days would sit far outside
      // the axis. The original range is kept as `blockedTimeframe` so the card
      // can still name it. A blocker that leaves nothing inside the window (e.g.
      // it ends at midnight of the viewed day) is dropped.
      const windowStart = date.hour(9).minute(0).second(0).millisecond(0);
      const windowEnd = date.hour(23).minute(59).second(0).millisecond(0);

      const processedBookings = allBookings
        .map(booking => {
          if (booking.bookingType === 'InternalBooking' && booking.status === 'blocked') {
            const bookingStart = dayjs(booking.startTime);
            const bookingEnd = dayjs(booking.endTime);
            const blockedTimeframe = `${bookingStart.format('DD.MM.YYYY HH:mm')} - ${bookingEnd.format(
              'DD.MM.YYYY HH:mm'
            )}`;

            const visibleStart = bookingStart.isBefore(windowStart) ? windowStart : bookingStart;
            const visibleEnd = bookingEnd.isAfter(windowEnd) ? windowEnd : bookingEnd;
            if (!visibleEnd.isAfter(visibleStart)) {
              return null;
            }
            if (visibleStart.isSame(bookingStart) && visibleEnd.isSame(bookingEnd)) {
              return { ...booking, blockedTimeframe };
            }
            return {
              ...booking,
              startTime: visibleStart.toISOString(),
              endTime: visibleEnd.toISOString(),
              eventDurationMS: visibleEnd.diff(visibleStart),
              blockedTimeframe,
            };
          }
          return booking;
        })
        .filter(Boolean);

      // Sort bookings by start time (eventStart for Regiondo, startTime for Internal)
      processedBookings.sort((a, b) => {
        const startA = a.bookingType === 'RegiondoBooking' ? a.eventStart : a.startTime;
        const startB = b.bookingType === 'RegiondoBooking' ? b.eventStart : b.startTime;
        return new Date(startA) - new Date(startB);
      });

      // Sort bookings into rows (resource-aware: same resource prefers same row)
      const { bookingsShown, rowCount: tempRowCount } = assignBookingRows(processedBookings);

      const capacityData = await loadCapacityData();

      if (dayDataFetchIdRef.current !== fetchId) {
        return;
      }

      setRowCount(tempRowCount);
      setBookings(bookingsShown);

      if (capacityData?.success) {
        setCapacity(capacityData.data);
      } else {
        setCapacity(null);
      }

      if (showDayLoadingState && loadKey) {
        setLoadedDayKey(loadKey);
      }
    } catch (error) {
      if (dayDataFetchIdRef.current !== fetchId) {
        return;
      }
      if (showDayLoadingState) {
        setDayDataError(error);
        toast.error('Fehler beim Laden der Buchungen');
      }
      console.error('Error fetching bookings:', error);
      return null;
    } finally {
      if (showDayLoadingState && dayDataFetchIdRef.current === fetchId) {
        setIsDayDataLoading(false);
      }
    }
  };

  fetchBookingsRef.current = fetchBookings;

  // Join the Socket.io room of the currently viewed entertainment + day so the
  // backend can send per-booking deltas and revalidation events for exactly this
  // view. Re-runs on reconnect (socketCtx changes with the connection state) so
  // the room is re-joined after a connection loss.
  useEffect(() => {
    if (!socketCtx || typeof socketCtx.emit !== 'function') return undefined;
    if (!entertainment || !date) return undefined;
    const dateStr = date.format ? date.format('YYYY-MM-DD') : String(date);
    socketCtx.emit('join_booking_day', { entertainmentId: entertainment, date: dateStr });
    return () => {
      socketCtx.emit('leave_booking_day', { entertainmentId: entertainment, date: dateStr });
    };
  }, [socketCtx, socketCtx?.isConnected, entertainment, date]);

  // Refetch the day when the backend signals (over the websocket) that the cached
  // booking list for the currently viewed entertainment + day was reconciled in the
  // background and actually changed. When backend booking-list caching is disabled the
  // event is simply never emitted, so this is a no-op.
  useEffect(() => {
    if (!socketCtx || typeof socketCtx.on !== 'function') return undefined;
    const off = socketCtx.on('data:revalidated', payload => {
      if (!payload || payload.scope !== 'internalBookingDay') return;
      const curEnt = latestRef.current.entertainment;
      const curDate = latestRef.current.date?.format
        ? latestRef.current.date.format('YYYY-MM-DD')
        : '';
      if (String(payload.entertainmentId) === String(curEnt) && payload.date === curDate) {
        fetchBookingsRef.current?.();
      }
    });
    return () => {
      if (typeof off === 'function') off();
    };
  }, [socketCtx]);

  // Debounced capacity refresh after socket deltas: the delta already patched the
  // booking list, but the capacity panel is computed backend-side and needs its own
  // (cheap) reload. One trailing reload per delta burst.
  const capacityRefreshTimerRef = useRef(null);
  const scheduleCapacityRefresh = useCallback(() => {
    if (capacityRefreshTimerRef.current) return;
    capacityRefreshTimerRef.current = setTimeout(async () => {
      capacityRefreshTimerRef.current = null;
      try {
        const capacityData = await loadCapacityDataRef.current?.();
        if (capacityData?.success) {
          setCapacity(capacityData.data);
        }
      } catch {
        // keep the last known capacity on a failed refresh
      }
    }, 3000);
  }, []);

  useEffect(
    () => () => {
      if (capacityRefreshTimerRef.current) clearTimeout(capacityRefreshTimerRef.current);
    },
    []
  );

  // Apply a per-booking delta (upsert/remove in day-list shape) to the local list
  // without refetching the whole day.
  const applyBookingDelta = useCallback(payload => {
    const current = bookingsRef.current || [];
    const incoming = payload.booking;
    const id = String(incoming._id);
    const showCanceledNow = Boolean(latestRef.current.showCanceled);

    let next;
    if (payload.type === 'remove') {
      next = current.filter(b => String(b._id) !== id);
      if (next.length === current.length) return;
    } else {
      // Mirror the server-side day-list criteria: canceled / zero-participant
      // bookings are hidden unless the canceled toggle is active.
      const hidden =
        !showCanceledNow && (incoming.status === 'canceled' || !(incoming.participantCount > 0));
      const exists = current.some(b => String(b._id) === id);
      if (hidden) {
        if (!exists) return;
        next = current.filter(b => String(b._id) !== id);
      } else {
        const patched = { ...incoming, bookingType: 'InternalBooking' };
        next = exists
          ? current.map(b => (String(b._id) === id ? { ...b, ...patched } : b))
          : [...current, patched];
      }
    }

    next.sort((a, b) => {
      const startA = a.bookingType === 'RegiondoBooking' ? a.eventStart : a.startTime;
      const startB = b.bookingType === 'RegiondoBooking' ? b.eventStart : b.startTime;
      return new Date(startA) - new Date(startB);
    });

    const { bookingsShown, rowCount: tempRowCount } = assignBookingRows(next);
    setRowCount(tempRowCount);
    setBookings(bookingsShown);
  }, []);

  // Debounced full refetch for delta cases that cannot be patched locally. Deltas
  // arrive once per write, so a busy day could otherwise trigger a refetch storm on
  // filtered views.
  const fallbackRefetchTimerRef = useRef(null);
  const scheduleFallbackRefetch = useCallback(() => {
    if (fallbackRefetchTimerRef.current) return;
    fallbackRefetchTimerRef.current = setTimeout(() => {
      fallbackRefetchTimerRef.current = null;
      fetchBookingsRef.current?.();
    }, 1500);
  }, []);

  useEffect(
    () => () => {
      if (fallbackRefetchTimerRef.current) clearTimeout(fallbackRefetchTimerRef.current);
    },
    []
  );

  // Live per-booking updates for the viewed day: patch the list locally instead of
  // refetching. Filtered views and blockers (multi-day timeframe post-processing)
  // fall back to a (debounced) full refetch.
  useEffect(() => {
    if (!socketCtx || typeof socketCtx.on !== 'function') return undefined;
    const off = socketCtx.on('internalBookingDay:delta', payload => {
      if (!payload || !payload.booking) return;
      const curEnt = latestRef.current.entertainment;
      const curDate = latestRef.current.date?.format
        ? latestRef.current.date.format('YYYY-MM-DD')
        : '';
      if (String(payload.entertainmentId) !== String(curEnt) || payload.date !== curDate) return;

      const isBlockerUpsert = payload.type !== 'remove' && payload.booking.status === 'blocked';
      if (latestRef.current.currentFilter !== 'all' || isBlockerUpsert) {
        scheduleFallbackRefetch();
        return;
      }

      applyBookingDelta(payload);
      scheduleCapacityRefresh();
    });
    return () => {
      if (typeof off === 'function') off();
    };
  }, [socketCtx, applyBookingDelta, scheduleCapacityRefresh, scheduleFallbackRefetch]);

  const updateBooking = useCallback((id, update, complete = false) => {
    const currentBookings = bookingsRef.current;
    const updatedBookings = currentBookings.map(booking => {
      if (booking._id == id) {
        let updated;
        if (complete) {
          updated = { ...update };
        } else {
          // Handle nested updates properly
          updated = { ...booking };
          Object.keys(update).forEach(key => {
            if (
              typeof update[key] === 'object' &&
              update[key] !== null &&
              !Array.isArray(update[key])
            ) {
              updated[key] = { ...updated[key], ...update[key] };
            } else {
              updated[key] = update[key];
            }
          });
        }
        if (
          booking.bookingType === 'InternalBooking' &&
          updated.bookingType !== 'InternalBooking'
        ) {
          updated.bookingType = booking.bookingType;
        }

        return updated;
      }
      return booking;
    });

    // Check if any booking was marked as 'removed' (indicates resource/time change)
    const hasRemovedBooking = updatedBookings.some(booking => booking.status === 'removed');

    if (hasRemovedBooking) {
      fetchBookingsRef.current();
      return;
    }

    // Filter out bookings with status 'removed'
    const filteredBookings = updatedBookings.filter(booking => booking.status !== 'removed');

    // Re-assign rows (resource-aware: same resource prefers same row)
    const { bookingsShown, rowCount: tempRowCount } = assignBookingRows(filteredBookings);

    setRowCount(tempRowCount);
    setBookings(bookingsShown);
  }, []);

  const handleBranchChange = event => {
    setBranch(event.target.value);
    sessionStorage.setItem('branch', event.target.value);
  };

  const handleEntertainmentChange = event => {
    const selectedEntertainment = event.target.value;
    setEntertainment(selectedEntertainment);
    sessionStorage.setItem('entertainment', selectedEntertainment);

    // Set the entertainment type based on the selected entertainment
    const foundEntertainment = entertainmentOptions.find(ent => ent.id === selectedEntertainment);
    if (foundEntertainment) {
      setEntertainmentType(foundEntertainment.label);
    }
  };

  useEffect(() => {
    const f = async () => {
      // get branches for selection
      const branches = await getBranches();

      const shownBranches = branches.map(branch => {
        return {
          label: branch.locationName,
          id: branch._id,
        };
      });

      setBranchOptions(shownBranches);

      const branchOptionSaved = sessionStorage.getItem('branch');
      const matchingBranch = shownBranches.find(branch => branch.id === branchOptionSaved);

      if (matchingBranch) {
        setBranch(branchOptionSaved); // Set to the saved branch id if it exists
      } else {
        setBranch(shownBranches[0]?.id || ''); // Fallback to the first branch's id or an empty string
      }
    };

    f();
  }, []);

  useEffect(() => {
    // The legacy blocker button is only shown with the dedicated special right;
    // defect-driven blockers are managed via the outage board ("Ausfall-Board").
    if (
      (checkRight('internalBooking:special:block') || canBlockDirect) &&
      checkRight('internalBooking:special:showBlockerButton')
    ) {
      setCanBlock(true);
    }
  }, [checkRight, canBlockDirect]);

  useEffect(() => {
    if (branch == '') return;

    const f = async () => {
      const allEntertainments = await getAllEntertainmentsByBranch(branch);

      const shownEntertainments = allEntertainments.map(entertainment => {
        return {
          id: entertainment._id,
          label: entertainment.entertainmentName,
          ltSlotCapacity: entertainment.ltSlotCapacity,
          ltCapacityMode: entertainment.ltCapacityMode,
          laneCount: entertainment.laneCount,
          participantLaneDensity: entertainment.participantLaneDensity,
          pbSlotCapacity: entertainment.pbSlotCapacity,
          showRegiondoBookings: entertainment.showRegiondoBookings,
          showInternalBookings: entertainment.showInternalBookings,
          prepTimeWarnings: entertainment.prepTimeWarnings,
          prepTimeMinutes: entertainment.prepTimeMinutes,
          bookingWindowDistance: entertainment.bookingWindowDistance,
        };
      });

      setEntertainmentOptions(shownEntertainments);

      const entertainmentOptionSaved = sessionStorage.getItem('entertainment');
      const matchingEntertainment = shownEntertainments.find(
        entertainment => entertainment.id === entertainmentOptionSaved
      );

      if (matchingEntertainment) {
        setEntertainment(entertainmentOptionSaved); // Set to the saved branch id if it exists
      } else {
        setEntertainment(shownEntertainments[0]?.id || ''); // Fallback to the first branch's id or an empty string
      }
    };

    f();
  }, [branch]);

  useEffect(() => {
    if (entertainment == '') return;
    setBookings([]);
    setPendingShift(null); // Reset pending shift on entertainment change

    // set type and capacity options
    const foundEntertainment = entertainmentOptions.find(ent => ent.id === entertainment);

    setEntertainmentType(foundEntertainment.label);
    setShowInternalBookings(foundEntertainment.showInternalBookings);

    // Update entertainment context
    setCurrentEntertainment(foundEntertainment.label);
    setCurrentView('bookings');

    if (foundEntertainment.label === 'LaserZone') {
      setSlotCapacity(foundEntertainment.ltSlotCapacity);
    } else if (foundEntertainment.label === 'PaintballZone') {
      setSlotCapacity(foundEntertainment.pbSlotCapacity);
    } else if (foundEntertainment.label === 'PixelZone' && foundEntertainment.participantLaneDensity) {
      setSlotCapacity(foundEntertainment.laneCount || 1);
    } else if (foundEntertainment.participantLaneDensity && foundEntertainment.laneCount) {
      setSlotCapacity(foundEntertainment.laneCount);
    }
  }, [entertainment, setCurrentEntertainment, setCurrentView]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (entertainment !== '') {
      setPendingShift(null); // Reset pending shift on data fetch

      const shouldShowDayLoadingState = loadedDayKey !== currentDayKey;
      fetchBookings({
        showDayLoadingState: shouldShowDayLoadingState,
        loadKey: currentDayKey,
      });

      const interval = setInterval(() => {
        if (isCreateBookingModalOpenRef.current === false) {
          fetchBookings();
        }
      }, 30 * 1000);
      return () => clearInterval(interval);
    }
  }, [currentDayKey, entertainment]);

  useEffect(() => {
    sessionStorage.setItem('date', date.format('YYYY-MM-DD'));
  }, [date]);

  // Cleanup entertainment context when component unmounts
  useEffect(() => {
    return () => {
      setCurrentView('default');
      setCurrentEntertainment(null);
    };
  }, [setCurrentView, setCurrentEntertainment]);

  const handleDayChange = (interval, direction) => {
    if (direction == 'next') {
      setDate(currentDate => currentDate.add(interval, 'day'));
    } else if (direction == 'previous') {
      setDate(currentDate => currentDate.subtract(interval, 'day'));
    }
  };

  const handleTodayClick = () => {
    setDate(dayjs());
  };

  const handleDateChange = newValue => {
    if (newValue?.isValid?.()) {
      setDate(newValue);
    }
  };

  const handleListSwitch = event => {
    setTableView(event.target.checked);
    sessionStorage.setItem('tableView', event.target.checked);
  };

  const handleFilterChange = event => {
    setCurrentFilter(event.target.value);
  };

  const openCreateBookingModal = () => {
    if (showInternalBookings === null) return;
    if (showInternalBookings) {
      setIsCreateBookingModalOpen(true);
    } else {
      window.open('https://www.regiondo.de/resellers/bookings/dashboardbooking', '_blank');
    }
  };

  const handleCreateBookingModalClose = () => {
    setIsCreateBookingModalOpen(false);
  };

  const handleCloseModal = useCallback(() => {
    setDetailsModalOpen(false);
    setModalBookingId(null);
    setModalBorderColor(null);
  }, []);

  const handleOpenModal = useCallback((bookingId, borderColor) => {
    setModalBookingId(bookingId);
    setModalBorderColor(borderColor);
    setDetailsModalOpen(true);
  }, []);

  const handleBlockerModalClose = () => {
    setBlockerModalOpen(false);
  };

  const handleBlockerModalOpen = () => {
    setBlockerModalOpen(true);
  };

  const handleBlockerSuccess = () => {
    fetchBookings(); // fetch all bookings, since the user might go ahead and check the capacity NOW -> all bookings should be refetched
  };

  const isBookingReadyForShift = useCallback(booking => {
    const et = latestRef.current.entertainmentType;
    if (
      (et === 'KaraokeZone' || et === 'ExitZone') &&
      !booking.resourceId
    ) {
      return false;
    }
    return true;
  }, []);

  useEffect(() => {
    // Always update to opening grid offset, no resource offset here
    const openingGridOffset = capacity?.gridOffset || 0;
    setCurrentShiftOffset(openingGridOffset);
  }, [capacity?.gridOffset]);

  const handleDragStart = useCallback((booking, showAvailableTimes = true) => {
    if (!isBookingReadyForShift(booking)) {
      setAssignResourceModalOpen(true);
      return false;
    }

    const { resourceOffsets: resOffsets, capacity: cap, entertainmentOptions: entOpts, entertainment: ent } = latestRef.current;

    const rawResourceId = booking?.resourceId?._id || booking?.resourceId;
    const resourceKey =
      (rawResourceId && rawResourceId.toString ? rawResourceId.toString() : rawResourceId) || '';
    const resourceOffsetMinutes =
      resourceKey && resOffsets[resourceKey] ? resOffsets[resourceKey] : 0;

    const openingGridOffset = cap?.gridOffset || 0;
    const interval =
      entOpts.find(e => e.id === ent)?.bookingWindowDistance || 15;
    const totalOffset = (resourceOffsetMinutes + openingGridOffset) % interval;
    setCurrentShiftOffset(totalOffset);

    const f = async () => {
      // Increment the session ID for each new drag operation.
      const currentDragSessionId = ++dragSessionId.current;

      // Only set loading/show times if we should display them
      if (showAvailableTimes) {
        setAvailableShiftTimes(null);
        availableShiftTimesRef.current = null;
        setIsLoadingShiftTimes(true);
      }

      try {
        const durationInMinutes = booking.eventDurationMS / (1000 * 60);
        setDraggedBookingDuration(durationInMinutes);

        const timesRes = await getAvailableShiftTimes({
          bookingId: booking._id,
          date: dayjs(latestRef.current.date).format('YYYY-MM-DD'),
          allowOverbook: canOverbook,
        });
        const times = normalizeShiftTimesResponse(timesRes);

        // Only update if this is still the active drag operation.
        if (dragSessionId.current === currentDragSessionId) {
          // Store the times but only show them if we should
          if (showAvailableTimes) {
            setAvailableShiftTimes(times || []);
          }
          availableShiftTimesRef.current = times || [];
          // Store times for later use even if not showing them yet
          prefetchedTimes.current = times || [];
        }
      } catch (error) {
        toast.error('Fehler beim Laden der verfügbaren Zeitslots');
        console.error('Error fetching available shift times:', error);
        // Only update if this is still the active drag operation.
        if (dragSessionId.current === currentDragSessionId) {
          if (showAvailableTimes) {
            setAvailableShiftTimes([]); // Set to empty on error to indicate unavailability
          }
          availableShiftTimesRef.current = [];
          prefetchedTimes.current = [];
        }
      } finally {
        if (dragSessionId.current === currentDragSessionId && showAvailableTimes) {
          setIsLoadingShiftTimes(false);
        }
      }
    };
    f();
    return true;
  }, [isBookingReadyForShift, getAvailableShiftTimes, canOverbook]);

  const handleDragActivated = useCallback(() => {
    // Show the prefetched times when long-press is actually activated
    if (prefetchedTimes.current) {
      const normalized = normalizeShiftTimesResponse(prefetchedTimes.current);
      setAvailableShiftTimes(normalized);
      availableShiftTimesRef.current = normalized;
      setIsLoadingShiftTimes(false);
    } else {
      // Data might still be loading, set up a polling mechanism
      const pollForData = () => {
        if (prefetchedTimes.current) {
          const normalized = normalizeShiftTimesResponse(prefetchedTimes.current);
          setAvailableShiftTimes(normalized);
          availableShiftTimesRef.current = normalized;
          setIsLoadingShiftTimes(false);
        } else {
          // Check again in 50ms
          setTimeout(pollForData, 50);
        }
      };
      setIsLoadingShiftTimes(true);
      pollForData();
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    dragSessionId.current++;
    setAvailableShiftTimes(null);
    setIsLoadingShiftTimes(false);
    setDraggedBookingDuration(0);
    setCurrentShiftOffset(latestRef.current.capacity?.gridOffset || 0);
    prefetchedTimes.current = null;
    availableShiftTimesRef.current = null;
  }, []);

  const handleBookingShift = useCallback(
    (booking, newTimeISO) => {
      const originalStartTime = dayjs(booking.startTime);
      const newTime = dayjs(newTimeISO);

      const correctNewStartTime = dayjs(latestRef.current.date)
        .hour(newTime.hour())
        .minute(newTime.minute())
        .second(0)
        .millisecond(0);

      // If a booking is dragged but dropped at the same time, do nothing and revert.
      // The "click" case is handled separately by `onCardClick` in the card component.
      if (originalStartTime.isSame(correctNewStartTime, 'minute')) {
        return false;
      }

      const newStartTime = correctNewStartTime.toISOString();

      const targetTimeKey = correctNewStartTime.format('HH:mm');
      const timesSource = Array.isArray(availableShiftTimesRef.current)
        ? availableShiftTimesRef.current
        : Array.isArray(prefetchedTimes.current)
        ? normalizeShiftTimesResponse(prefetchedTimes.current)
        : [];
      const matching = timesSource.find(t => {
        if (typeof t === 'string') return t === targetTimeKey;
        return t?.startTime === targetTimeKey;
      });
      // Without a loaded list of times the capacity of the target is unknown —
      // the shift is then attempted as-is and the backend decides.
      const overbookedBy = Number(matching?.overbookedBy) || 0;
      const requiresOverbook =
        timesSource.length > 0 && (!matching || overbookedBy > 0);

      setPendingShift({ bookingId: booking._id, newStartTime });
      setOverbookConfirmed(false);
      setShiftConfirmation({
        open: true,
        booking,
        newStartTime,
        requiresOverbook,
        overbookedBy: matching ? overbookedBy : 0,
      });
      return true;
    },
    []
  );

  const handleShiftCancel = () => {
    setShiftConfirmation({
      open: false,
      booking: null,
      newStartTime: null,
      requiresOverbook: false,
      overbookedBy: 0,
    });
    setOverbookConfirmed(false);
    setPendingShift(null);
  };

  const handleShiftConfirm = async () => {
    const { booking, newStartTime, requiresOverbook } = shiftConfirmation;
    if (!booking || !newStartTime) return;

    // Close the dialog, but keep the optimistic state
    setShiftConfirmation({
      open: false,
      booking: null,
      newStartTime: null,
      requiresOverbook: false,
      overbookedBy: 0,
    });
    setOverbookConfirmed(false);

    const toastMessage = `Die Verschiebungsanfrage für ${booking.firstName} ${booking.lastName} wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;
    const successToastMessage = `Buchung für ${booking.firstName} ${
      booking.lastName
    } erfolgreich von ${dayjs(booking.startTime).format('DD.MM.YYYY HH:mm')} auf ${dayjs(
      newStartTime
    ).format('DD.MM.YYYY HH:mm')} verschoben`;

    const displayInfo = {
      Typ: 'Buchung verschieben',
      Kunde: `${booking.firstName} ${booking.lastName}`,
      Von: `${dayjs(booking.startTime).format('DD.MM.YYYY HH:mm')}`,
      Nach: `${dayjs(newStartTime).format('DD.MM.YYYY HH:mm')}`,
    };

    try {
      const result = await shiftBooking(
        {
          bookingId: booking._id,
          newDate: newStartTime,
          allowOverbook: Boolean(canOverbook && requiresOverbook && overbookConfirmed),
        },
        toastMessage,
        () => setPendingShift(null), // onStored callback
        displayInfo,
        async () => {
          await fetchBookings();
          setPendingShift(null);
        }, // onSuccess callback
        successToastMessage
      );

      if (result) {
        // On success, fetch the new state. The card is already in place.
        await fetchBookings();
        toast.success('Buchung erfolgreich verschoben');
      } else {
        // This case might be triggered by offline queueing, not necessarily a direct failure.
        // If there was an actual error, the catch block will handle it.
        // If not, the optimistic state will be reverted by onStored/onSuccess later.
      }
    } catch (error) {
      // On failure, revert the optimistic state immediately.
      toast.error('Verschieben der Buchung fehlgeschlagen');
      setPendingShift(null);
    } finally {
      // Ensure pending shift is cleared if it hasn't been by other means
      // This is a fallback, good practice for complex async flows.
      if (pendingShift?.bookingId === booking._id) {
        setPendingShift(null);
      }
    }
  };

  const foundEntertainment = useMemo(
    () => entertainmentOptions.find(ent => ent.id === entertainment),
    [entertainmentOptions, entertainment]
  );

  const shouldShowDayDataPlaceholder = entertainment !== '' && loadedDayKey !== currentDayKey;

  const renderDayLoadingState = () => <DotsLoader />;

  const renderDayErrorState = () => {
    const dateLabel = date?.format ? date.format('DD.MM.YYYY') : '';

    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 2, sm: 3 },
          marginTop: { xs: 4, sm: 6 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: 'min(980px, 100%)',
            padding: { xs: 2, sm: 3 },
            borderRadius: 2,
          }}
        >
          <Stack spacing={1.25} alignItems='center' textAlign='center'>
            <EventNoteIcon sx={{ fontSize: 56, color: 'text.secondary', opacity: 0.65 }} />
            <Typography variant='h6' sx={{ fontWeight: 650 }}>
              Fehler beim Laden der Buchungen
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {dateLabel}
              {entertainmentType ? ` • ${entertainmentType}` : ''}
            </Typography>
            <Button
              variant='contained'
              onClick={() =>
                fetchBookings({
                  showDayLoadingState: true,
                  loadKey: currentDayKey,
                })
              }
              sx={{ marginTop: 1 }}
            >
              Erneut versuchen
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  };

  const bookingsWithPendingShift = useMemo(() => {
    if (!pendingShift) {
      return bookings;
    }
    return bookings.map(b => {
      if (b._id === pendingShift.bookingId) {
        return { ...b, startTime: pendingShift.newStartTime };
      }
      return b;
    });
  }, [bookings, pendingShift]);

  const isShiftable = useCallback(booking => {
    if (!booking) return false;

    if (booking.status === 'blocked' || booking.bookingType === 'RegiondoBooking') {
      return false;
    }

    const isWaitingForPayment = booking.status === 'waitingForPayment';
    const isCanceled = booking.status === 'canceled' || booking.status === 'noShow';
    const isPresent = !!booking.presentSince;

    if (isPresent) {
      const presentSince = dayjs(booking.presentSince);
      const expiresAt = presentSince.isValid() ? presentSince.add(1, 'hour') : null;
      const isExpired = !expiresAt || dayjs().isAfter(expiresAt);
      if (isExpired) return false;
    }

    return !isWaitingForPayment && !isCanceled;
  }, []);

  const renderEntertainmentComponent = entertainmentName => {
    switch (entertainmentName) {
      case 'LaserZone':
        return (
          <GridLasertag
            bookings={bookingsWithPendingShift}
            rowCount={rowCount}
            date={date}
            slotCapacity={slotCapacity}
            updateBooking={updateBooking}
            handleOpenModal={handleOpenModal}
            capacity={capacity}
            onBookingShift={handleBookingShift}
            entertainment={foundEntertainment}
            onDragStart={handleDragStart}
            onDragActivated={handleDragActivated}
            onDragEnd={handleDragEnd}
            availableShiftTimes={availableShiftTimes}
            draggedBookingDuration={draggedBookingDuration}
            isShiftable={isShiftable}
            isLoadingShiftTimes={isLoadingShiftTimes}
          />
        );
      case 'AxeZone':
        return (
          <GridAxe
            bookings={bookingsWithPendingShift}
            rowCount={rowCount}
            date={date}
            slotCapacity={foundEntertainment?.laneCount}
            updateBooking={updateBooking}
            handleOpenModal={handleOpenModal}
            capacity={capacity}
            onBookingShift={handleBookingShift}
            entertainment={foundEntertainment}
            onDragStart={handleDragStart}
            onDragActivated={handleDragActivated}
            onDragEnd={handleDragEnd}
            availableShiftTimes={availableShiftTimes}
            draggedBookingDuration={draggedBookingDuration}
            isShiftable={isShiftable}
            isLoadingShiftTimes={isLoadingShiftTimes}
          />
        );
      case 'ExitZone':
        return (
          <GridExit
            bookings={bookingsWithPendingShift}
            rowCount={rowCount}
            date={date}
            updateBooking={updateBooking}
            handleOpenModal={handleOpenModal}
            entertainmentId={entertainment}
            capacity={capacity}
            onBookingShift={handleBookingShift}
            entertainment={foundEntertainment}
            onDragStart={handleDragStart}
            onDragActivated={handleDragActivated}
            onDragEnd={handleDragEnd}
            availableShiftTimes={availableShiftTimes}
            draggedBookingDuration={draggedBookingDuration}
            isShiftable={isShiftable}
            isLoadingShiftTimes={isLoadingShiftTimes}
            isBookingReadyForShift={isBookingReadyForShift}
            shiftTimeOffsetMinutes={currentShiftOffset}
            resourceOffsetMap={resourceOffsets}
          />
        );
      case 'GlowZone':
        return (
          <GridGolfOrdered
            bookings={bookingsWithPendingShift}
            rowCount={rowCount}
            date={date}
            updateBooking={updateBooking}
            handleOpenModal={handleOpenModal}
            entertainmentId={entertainment}
            capacity={capacity}
            onBookingShift={handleBookingShift}
            entertainment={foundEntertainment}
            onDragStart={handleDragStart}
            onDragActivated={handleDragActivated}
            onDragEnd={handleDragEnd}
            availableShiftTimes={availableShiftTimes}
            draggedBookingDuration={draggedBookingDuration}
            isShiftable={isShiftable}
            isLoadingShiftTimes={isLoadingShiftTimes}
            isBookingReadyForShift={isBookingReadyForShift}
            useVerticalForInternal={useVerticalForInternal}
            showAllSlots={false}
            onIntervalModeChange={setIsIntervalModeActive}
          />
        );
      case 'BashZone':
        return (
          <GridBashOrdered
            bookings={bookingsWithPendingShift}
            rowCount={rowCount}
            date={date}
            updateBooking={updateBooking}
            handleOpenModal={handleOpenModal}
            entertainmentId={entertainment}
            capacity={capacity}
            onBookingShift={handleBookingShift}
            entertainment={foundEntertainment}
            onDragStart={handleDragStart}
            onDragActivated={handleDragActivated}
            onDragEnd={handleDragEnd}
            availableShiftTimes={availableShiftTimes}
            draggedBookingDuration={draggedBookingDuration}
            isShiftable={isShiftable}
            isLoadingShiftTimes={isLoadingShiftTimes}
            isBookingReadyForShift={isBookingReadyForShift}
            useVerticalForInternal={useVerticalForInternal}
            showAllSlots={false}
            onIntervalModeChange={setIsIntervalModeActive}
          />
        );
      case 'PaintballZone':
        return (
          <GridPaintball
            bookings={bookingsWithPendingShift}
            rowCount={rowCount}
            date={date}
            slotCapacity={slotCapacity}
            updateBooking={updateBooking}
            capacity={capacity}
            handleOpenModal={handleOpenModal}
            onBookingShift={handleBookingShift}
            entertainment={foundEntertainment}
            onDragStart={handleDragStart}
            onDragActivated={handleDragActivated}
            onDragEnd={handleDragEnd}
            availableShiftTimes={availableShiftTimes}
            draggedBookingDuration={draggedBookingDuration}
            isShiftable={isShiftable}
            isLoadingShiftTimes={isLoadingShiftTimes}
          />
        );
      case 'PixelZone':
        return (
          <GridPixel
            bookings={bookingsWithPendingShift}
            rowCount={rowCount}
            date={date}
            slotCapacity={slotCapacity}
            updateBooking={updateBooking}
            handleOpenModal={handleOpenModal}
            capacity={capacity}
            onBookingShift={handleBookingShift}
            entertainment={foundEntertainment}
            onDragStart={handleDragStart}
            onDragActivated={handleDragActivated}
            onDragEnd={handleDragEnd}
            availableShiftTimes={availableShiftTimes}
            draggedBookingDuration={draggedBookingDuration}
            isShiftable={isShiftable}
            isLoadingShiftTimes={isLoadingShiftTimes}
          />
        );
      case 'RallyeZone':
        return (
          <GridRallye
            bookings={bookingsWithPendingShift}
            rowCount={rowCount}
            date={date}
            capacity={capacity}
            slotCapacity={slotCapacity}
            updateBooking={updateBooking}
            handleOpenModal={handleOpenModal}
            onBookingShift={handleBookingShift}
            entertainment={foundEntertainment}
            onDragStart={handleDragStart}
            onDragActivated={handleDragActivated}
            onDragEnd={handleDragEnd}
            availableShiftTimes={availableShiftTimes}
            draggedBookingDuration={draggedBookingDuration}
            isShiftable={isShiftable}
            isLoadingShiftTimes={isLoadingShiftTimes}
          />
        );
      case 'KaraokeZone':
        return (
          <GridKaraoke
            bookings={bookingsWithPendingShift}
            rowCount={rowCount}
            date={date}
            slotCapacity={slotCapacity}
            updateBooking={updateBooking}
            handleOpenModal={handleOpenModal}
            entertainmentId={entertainment}
            capacity={capacity}
            onBookingShift={handleBookingShift}
            entertainment={foundEntertainment}
            onDragStart={handleDragStart}
            onDragActivated={handleDragActivated}
            onDragEnd={handleDragEnd}
            availableShiftTimes={availableShiftTimes}
            draggedBookingDuration={draggedBookingDuration}
            isShiftable={isShiftable}
            isLoadingShiftTimes={isLoadingShiftTimes}
            isBookingReadyForShift={isBookingReadyForShift}
            shiftTimeOffsetMinutes={currentShiftOffset}
            resourceOffsetMap={resourceOffsets}
          />
        );
      default:
        return (
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='center'
            sx={{ width: '100%', marginTop: '50px' }}
          >
            <HourglassTopRoundedIcon sx={{ fontSize: '100', marginRight: '10px' }} />
            <Typography variant='h4' component='div' align='center'>
              Ansicht noch in Entwicklung...
            </Typography>
          </Stack>
        );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        isolation: 'isolate',
      }}
    >
      {entertainmentType === 'LaserZone' && checkRight('lasertagGame:read') && (
        <LaserZoneGameStatus entertainment={entertainment} />
      )}

      {/* Filter-Abschnitt */}
      <Paper
        ref={filterBarRef}
        sx={{
          marginBottom: 2,
          padding: 2,
          flexShrink: 0,
          position: 'sticky',
          top: { xs: 56, sm: 64 },
          zIndex: theme => theme.zIndex.appBar,
          backgroundImage: 'none !important',
          background: theme => theme.palette.background.default,
        }}
        elevation={6}
      >
        <Stack
          direction='row'
          spacing={1.5}
          alignItems='center'
          flexWrap='wrap'
          useFlexGap
          sx={{
            '& > *': {
              flexShrink: 0,
            },
          }}
        >
          {/* Datum + Navigation */}
          <DatePicker
            label='Datum'
            value={date}
            format='DD.MM.YYYY'
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  width: { xs: '100%', sm: showWeekdayInDatePicker ? 210 : 140 },
                  minWidth: showWeekdayInDatePicker ? 220 : 150,
                  flex: { xs: 1, sm: 'none' },
                },
                InputProps: showWeekdayInDatePicker
                  ? {
                      startAdornment: (
                        <InputAdornment position='start'>{date.format('ddd')}</InputAdornment>
                      ),
                    }
                  : undefined,
              },
            }}
            onChange={handleDateChange}
          />

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 0.5,
              flex: { xs: 1, sm: 'none' },
            }}
          >
            {isWeekNavigationVisible && (
              <IconButton size='small' onClick={() => handleDayChange(7, 'previous')}>
                <KeyboardDoubleArrowLeftIcon fontSize='small' />
              </IconButton>
            )}
            {isDayNavigationVisible && (
              <IconButton size='small' onClick={() => handleDayChange(1, 'previous')}>
                <NavigateBeforeIcon fontSize='small' />
              </IconButton>
            )}
            <Button
              size='small'
              color='inherit'
              onClick={handleTodayClick}
              sx={{ mx: 0.5, minWidth: 'auto', px: 1 }}
              startIcon={<TodayIcon fontSize='small' />}
            >
              Heute
            </Button>
            {isDayNavigationVisible && (
              <IconButton size='small' onClick={() => handleDayChange(1, 'next')}>
                <NavigateNextIcon fontSize='small' />
              </IconButton>
            )}
            {isWeekNavigationVisible && (
              <IconButton size='small' onClick={() => handleDayChange(7, 'next')}>
                <KeyboardDoubleArrowRightIcon fontSize='small' />
              </IconButton>
            )}
          </Box>

          {/* Branch + Entertainment */}
          {branchOptions.length > 1 && (
            <FormControl
              size='small'
              sx={{
                width: { xs: '100%', sm: 140 },
                minWidth: 120,
                flex: { xs: 1, sm: 'none' },
              }}
            >
              <InputLabel>Branch</InputLabel>
              <Select value={branch} label='Branch' onChange={handleBranchChange}>
                {branchOptions.map(br => (
                  <MenuItem key={br.id} value={br.id}>
                    {br.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl
            size='small'
            sx={{
              width: { xs: '100%', sm: 160 },
              minWidth: 140,
              flex: { xs: 1, sm: 'none' },
            }}
          >
            <InputLabel>Entertainment</InputLabel>
            <Select
              value={entertainment}
              label='Entertainment'
              onChange={handleEntertainmentChange}
            >
              {entertainmentOptions.map(ent => (
                <MenuItem key={ent.id} value={ent.id}>
                  {ent.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Filter */}
          <FormControl
            size='small'
            sx={{
              width: { xs: '100%', sm: 120 },
              minWidth: 100,
              flex: { xs: 1, sm: 'none' },
            }}
          >
            <InputLabel>Filter</InputLabel>
            <Select value={currentFilter} label='Filter' onChange={handleFilterChange}>
              {filterOptions.map(ent => (
                <MenuItem key={ent.id} value={ent.id}>
                  {ent.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Switches Group - Together */}
          <Stack
            direction='column'
            spacing={-1}
            alignItems='center'
            sx={{
              flexShrink: 0,
              flex: { xs: 1, sm: 'none' },
              justifyContent: { xs: 'center', sm: 'flex-start' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Stack
              direction='row'
              spacing={0.25}
              alignItems='center'
              sx={{
                justifyContent: 'space-between',
                width: '100%',
                paddingY: '2px',
              }}
            >
              <Typography variant='body2' sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                Stornierte
              </Typography>
              <Switch
                checked={showCanceled}
                onChange={() => setShowCanceled(!showCanceled)}
                size='small'
                sx={{ transform: 'scale(0.8)' }}
              />
            </Stack>

            <Stack
              direction='row'
              spacing={0.25}
              alignItems='center'
              sx={{
                justifyContent: 'space-between',
                width: '100%',
                paddingY: '2px',
              }}
            >
              <Typography variant='body2' sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                Liste
              </Typography>
              <Switch
                checked={tableView}
                onChange={handleListSwitch}
                size='small'
                sx={{ transform: 'scale(0.8)' }}
              />
            </Stack>
          </Stack>

          {foundEntertainment &&
            foundEntertainment.showInternalBookings &&
            (foundEntertainment.label === 'GlowZone' ||
              foundEntertainment.label === 'BashZone') && (
              <Stack
                direction='column'
                spacing={-1}
                alignItems='center'
                sx={{
                  flexShrink: 0,
                  flex: { xs: 1, sm: 'none' },
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <Stack
                  direction='row'
                  spacing={0.25}
                  alignItems='center'
                  sx={{
                    justifyContent: 'space-between',
                    width: '100%',
                    paddingY: '2px',
                  }}
                >
                  <Typography variant='body2' sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                    Zeitstrahl
                  </Typography>
                  <Switch
                    checked={useVerticalForInternal}
                    onChange={(_, checked) => {
                      setUseVerticalForInternal(checked);
                    }}
                    size='small'
                    sx={{ transform: 'scale(0.8)' }}
                  />
                </Stack>
                {isIntervalModeActive && null}
              </Stack>
            )}

          {/* Spacer for right alignment */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }} />

          {/* Action Buttons - Right aligned - Together */}
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            sx={{
              flexShrink: 0,
              flex: { xs: 1, sm: 'none' },
              justifyContent: { xs: 'space-between', sm: 'flex-start' },
            }}
          >
            {canBlock && showInternalBookings && (
              <IconButton
                color='warning'
                onClick={handleBlockerModalOpen}
                size='small'
                sx={{
                  padding: '4px 10px',
                  borderRadius: '5px',
                  border: '1px solid',
                  borderColor: 'warning.main',
                }}
              >
                <BlockIcon fontSize='small' />
                {isButtonTextVisible && (
                  <Typography
                    variant='body2'
                    sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', marginLeft: '5px' }}
                  >
                    {'Blocker'}
                  </Typography>
                )}
              </IconButton>
            )}

            <Button
              variant='contained'
              color='success'
              size='small'
              startIcon={<AddIcon fontSize='small' />}
              onClick={openCreateBookingModal}
              sx={{ minWidth: 'auto', px: 2, whiteSpace: 'nowrap' }}
              disabled={
                showInternalBookings === null ||
                !(
                  (showInternalBookings && checkRight('internalBooking:create:read')) ||
                  (!showInternalBookings && checkRight('booking:create'))
                )
              }
            >
              Neu
            </Button>
          </Stack>
        </Stack>
      </Paper>
      <Box
        sx={{
          flex: 1,
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {shouldShowDayDataPlaceholder ? (
          isDayDataLoading ? (
            renderDayLoadingState()
          ) : dayDataError ? (
            renderDayErrorState()
          ) : (
            renderDayLoadingState()
          )
        ) : !!bookings.length ? (
          <>
            {tableView ? (
              <BookingTable
                bookings={bookings}
                updateBooking={updateBooking}
                entertainmentType={entertainmentType}
              />
            ) : (
              renderEntertainmentComponent(entertainmentType)
            )}
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              marginTop: '80px',
              padding: '40px 20px',
            }}
          >
            <EventNoteIcon
              sx={{
                fontSize: '80px',
                color: 'text.secondary',
                marginBottom: '20px',
                opacity: 0.6,
              }}
            />
            <Typography
              variant='h5'
              component='div'
              align='center'
              sx={{
                color: 'text.secondary',
                marginBottom: '8px',
                fontWeight: 500,
              }}
            >
              Keine Buchungen vorhanden
            </Typography>
            <Typography
              variant='body1'
              component='div'
              align='center'
              sx={{
                color: 'text.secondary',
                opacity: 0.7,
                maxWidth: '400px',
              }}
            >
              Für den ausgewählten Zeitraum sind noch keine Buchungen eingetragen.
            </Typography>
          </Box>
        )}
      </Box>
      <CreateBlockerModal
        show={blockerModalOpen}
        onHide={handleBlockerModalClose}
        branchId={branch}
        entertainmentId={entertainment}
        onSuccess={handleBlockerSuccess}
        capacity={capacity?.capacity}
        entertainment={foundEntertainment}
        directMode={canBlockDirect}
      />
      <BookingDetailsModal
        open={detailsModalOpen}
        onClose={handleCloseModal}
        bookingId={modalBookingId}
        onUpdate={updateBooking}
        borderColor={modalBorderColor}
      />
      {isCreateBookingModalOpen && (
        <BookingProvider>
          <CreateBookingModal
            open={isCreateBookingModalOpen}
            onClose={handleCreateBookingModalClose}
            onSuccess={fetchBookings}
            onAfterCreate={({ bookingId }) => {
              if (bookingId) {
                setModalBookingId(bookingId);
                setModalBorderColor(null);
                setDetailsModalOpen(true);
              }
            }}
            branchId={branch}
            entertainmentId={entertainment}
          />
        </BookingProvider>
      )}
      <Dialog open={shiftConfirmation.open} onClose={handleShiftCancel}>
        <DialogTitle>Buchung verschieben</DialogTitle>
        <DialogContent>
          {shiftConfirmation.booking && shiftConfirmation.newStartTime && (
            <DialogContentText>
              Möchtest du die Buchung von{' '}
              <Box component='span' sx={{ fontWeight: 600 }}>
                {shiftConfirmation.booking.firstName} {shiftConfirmation.booking.lastName}
              </Box>{' '}
              von{' '}
              <Box component='span' sx={{ fontWeight: 600 }}>
                {dayjs(shiftConfirmation.booking.startTime).format('HH:mm')} Uhr
              </Box>{' '}
              auf{' '}
              <Box component='span' sx={{ fontWeight: 600 }}>
                {dayjs(shiftConfirmation.newStartTime).format('HH:mm')} Uhr
              </Box>{' '}
              verschieben?
            </DialogContentText>
          )}
          {/* The overbooking question is only asked when the capacity of the
              target actually requires it — a free slot is confirmed as before. */}
          {shiftConfirmation.requiresOverbook && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2' sx={{ color: 'warning.main' }}>
                {shiftConfirmation.overbookedBy > 0
                  ? `Zu dieser Zeit reicht die Kapazität nicht aus - ${
                      shiftConfirmation.overbookedBy
                    } ${shiftConfirmation.overbookedBy === 1 ? 'Platz' : 'Plätze'} über der Kapazität.`
                  : 'Zu diesem Zeitpunkt ist kein Slot verfügbar.'}
                {canOverbook
                  ? ' Das Verschieben ist nur mit Überbuchung möglich.'
                  : ' Für eine Überbuchung fehlt dir das Recht.'}
              </Typography>
              {canOverbook && (
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Switch
                      checked={overbookConfirmed}
                      onChange={e => setOverbookConfirmed(e.target.checked)}
                      color='warning'
                    />
                  }
                  label={
                    <Typography variant='body2' sx={{ color: 'warning.main' }}>
                      Überbuchung erlauben
                    </Typography>
                  }
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleShiftCancel}>Abbrechen</Button>
          <Button
            onClick={handleShiftConfirm}
            autoFocus
            disabled={
              shiftConfirmation.requiresOverbook && !(canOverbook && overbookConfirmed)
            }
          >
            Bestätigen
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={assignResourceModalOpen} onClose={() => setAssignResourceModalOpen(false)}>
        <DialogTitle>Kein Raum zugewiesen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bitte weise der Buchung einen Raum zu, bevor du die Buchung verschiebst.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignResourceModalOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Bookings;
